import { Button, Checkbox, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ButtonContainer, FieldSet, IDataTableColumn, showSuccess, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import {
  DogApi,
  DogsTransientCharacteristicsApi,
  TransientCharacteristicGridDto,
  TransientCharacteristicsSearch,
} from "../../../api";
import { CustomDataTable, ExportButton, SharepointButton } from "../../../components";
import { useDialog, useGlobalData } from "../../../contexts";
import { ERoutes } from "../../../ERoutes";
import { useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

export interface IDogTransientCharacteristicsProps {
  searchUnassignedCharacteristics: boolean;
}

const StyledCheckbox = styled(Checkbox)`
  font-size: 10pt;
  top: 6px;
`;

export const DogTransientCharacteristicList: React.FunctionComponent<IDogTransientCharacteristicsProps> = ({
  searchUnassignedCharacteristics,
}) => {
  const api = useApiService(DogsTransientCharacteristicsApi);
  const dogApi = useApiService(DogApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const { showDialogPromise } = useDialog();
  const [hideClosed, sethideClosed] = React.useState(true);
  const [sObjState, setsObjState] = React.useState<any>();
  const [selectionList, setselectionList] = React.useState([]);
  const editableIdsList = React.useMemo(() => [], []);
  const [selectAll, setselectAll] = React.useState(false);
  const [actionloading, setActionloading] = React.useState(false);

  const tableState = useGridState<any>({
    serverMode: true,
    availablePageSizes: [10],
    enablePagination: true,
    enableFilter: true,
    sortKeys: { dog: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: TransientCharacteristicsSearch) => {
      const s = TransientCharacteristicsSearch.fromJS({
        ...sObj,
        hideClosed: hideClosed,
        searchUnassignedCharacteristics: searchUnassignedCharacteristics,
        languageId: currentLanguage.languageId,
      });
      setsObjState(s);
      return api.baseSearch(s);
    },
    [api, currentLanguage.languageId, hideClosed, searchUnassignedCharacteristics]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    console.log("value", value);
    if (value) {
      selectionList.splice(0, selectionList.length, ...editableIdsList);
    } else {
      selectionList.splice(0, selectionList.length);
    }
    setselectionList([...selectionList]);
    console.log("selectionList ", selectionList);
    setselectAll(value);
  }, [editableIdsList, selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: TransientCharacteristicGridDto) => {
      if (!row.closedOn && !editableIdsList.find((c) => c === row.dogsCharacteristicsValuesTransientId)) {
        editableIdsList.push(row.dogsCharacteristicsValuesTransientId);
      }

      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.dogsCharacteristicsValuesTransientId) && !row.closedOn}
          onClick={() => {
            if (selectionList.includes(row.dogsCharacteristicsValuesTransientId)) {
              const index = selectionList.findIndex((e) => e === row.dogsCharacteristicsValuesTransientId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.dogsCharacteristicsValuesTransientId);
            }
            setselectionList([...selectionList]);
          }}
          disabled={!!row.closedOn}
        />
      );
    },
    [editableIdsList, selectionList]
  );
  const [shpLoading, setShpLoading] = React.useState(null);
  const openOrCreateInShp = React.useCallback(
    async (data: TransientCharacteristicGridDto) => {
      setShpLoading(data?.dogId);
      if (!!data?.sharepointUri) {
        window.open(data?.sharepointUri, "_blank");
        setShpLoading(null);
      } else {
        try {
          await dogApi.createInSharepoint(data?.dogId);
          search();
        } catch (e) {
          throw e;
        } finally {
          setShpLoading(null);
        }
      }
    },
    [dogApi, search]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} checked={selectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: (row: TransientCharacteristicGridDto) => columnCheckBox(row),
      },
      {
        header: () => t(ETLCodes.Dog),
        fieldName: "dog",
        render: (row: TransientCharacteristicGridDto) => {
          return (
            <Link target="_blank" to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
              {row.dog}
            </Link>
          );
        },
      },
      {
        header: () => t(ETLCodes.Characteristic),
        fieldName: "characteristic",
      },
      {
        header: () => t(ETLCodes.Value),
        fieldName: "healthValue",
      },
      {
        header: () => t(ETLCodes.SharePoint),
        fieldName: "_action",
        render: (row: TransientCharacteristicGridDto) => (
          <SharepointButton
            existsInShp={!!row?.sharepointUri}
            loading={shpLoading === row?.dogId}
            onClick={() => openOrCreateInShp(row)}
          />
        ),
      },
    ],
    [columnCheckBox, openOrCreateInShp, selectAll, shpLoading, t, toggleSelectAll] //Do not remove dependency, usefull for visual update
  );

  const action = React.useCallback(
    async (requestList: number[]) => {
      setActionloading(true);
      if (requestList.length === 0) {
        await showDialogPromise({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [{ resultType: "yes", text: t(ETLCodes.General_Ok), intent: Intent.PRIMARY }],
        });
      } else {
        if (searchUnassignedCharacteristics) {
          const dialogResult = await showDialogPromise({
            message: t(ETLCodes.AssignElementsQuestion),
            title: t(ETLCodes.Assign),
          });
          if (dialogResult === "yes") {
            await api.assign(requestList);
            selectionList.splice(0, selectionList.length);
            search();
          }
        } else {
          const dialogResult = await showDialogPromise({
            message: t(ETLCodes.ValidateElementsQuestion),
            title: t(ETLCodes.Validate),
          });
          if (dialogResult === "yes") {
            await api.validate(requestList);
            selectionList.forEach((id) => {
              var i = editableIdsList.indexOf(id);
              editableIdsList.splice(i);
            });
            setselectionList([]);
            editableIdsList.splice(0, editableIdsList.length);
            setselectAll(false);
            search();
          }
        }
      }
      setActionloading(false);
    },
    [api, editableIdsList, search, searchUnassignedCharacteristics, selectionList, showDialogPromise, t]
  );

  const refuse = React.useCallback(
    async (requestList: number[]) => {
      setActionloading(true);
      if (requestList.length === 0) {
        await showDialogPromise({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [{ resultType: "yes", text: t(ETLCodes.General_Ok), intent: Intent.PRIMARY }],
        });
      } else {
        const dialogResult = await showDialogPromise({
          message: t(ETLCodes.CloseTaskConfirmation),
          title: t(ETLCodes.Close),
        });
        if (dialogResult === "yes") {
          await api.close(requestList);
          showSuccess(t(ETLCodes.SaveSuccess));
          search();
          selectionList.forEach((id) => {
            var i = editableIdsList.indexOf(id);
            editableIdsList.splice(i);
          });
          setselectionList([]);
          setselectAll(false);
        }
      }
      setActionloading(false);
    },
    [api, editableIdsList, search, selectionList, showDialogPromise, t]
  );

  return (
    <FieldSet
      title={
        searchUnassignedCharacteristics
          ? t(ETLCodes.HealthCharacteristicsToAssign)
          : t(ETLCodes.HealthCharacteristicsToValidate)
      }
      rightElement={
        <ButtonContainer>
          <Button
            text={searchUnassignedCharacteristics ? t(ETLCodes.Assign) : t(ETLCodes.Validate)}
            onClick={() => action(selectionList)}
            intent={Intent.PRIMARY}
            minimal={true}
            icon={IconNames.TICK_CIRCLE}
            loading={actionloading}
          />
          {!searchUnassignedCharacteristics && (
            <Button
              text={t(ETLCodes.Refuse)}
              onClick={() => refuse(selectionList)}
              intent={Intent.PRIMARY}
              minimal={true}
              icon={IconNames.DELETE}
              loading={actionloading}
            />
          )}
          {!searchUnassignedCharacteristics && (
            <StyledCheckbox
              checked={hideClosed}
              onClick={() => {
                sethideClosed(!hideClosed);
                search();
              }}
              label={t(ETLCodes.HideClosed)}
            />
          )}
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonContainer>
      }
    >
      <CustomDataTable tableState={tableState} loading={loading || actionloading} columns={columns} />
    </FieldSet>
  );
};
